/**
 * Application-wide configuration settings
 * 
 * Configuration is loaded from backend /api/config endpoint on startup.
 * Environment variables (REACT_APP_*) are used as fallback for development/local builds.
 * 
 * This ensures all configuration is centralized in the backend for production deployments.
 */

// Environment-based configuration
const isProduction = process.env.NODE_ENV === 'production';

// Global config cache (loaded from backend)
let backendConfigCache = null;
let configLoadPromise = null;

/**
 * Load configuration from backend /api/config endpoint
 * @returns {Promise<Object>} Configuration object from backend
 */
async function loadBackendConfig() {
  // Return cached config if available
  if (backendConfigCache) {
    return backendConfigCache;
  }

  // Return existing promise if config is already being loaded
  if (configLoadPromise) {
    return configLoadPromise;
  }

  // Load config from backend
  configLoadPromise = (async () => {
    try {
      // Detect API base URL
      let apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      if (!apiBaseUrl && typeof window !== 'undefined') {
        const isDev = /localhost|127\.0\.0\.1/.test(window.location.hostname);
        if (isDev) {
          apiBaseUrl = 'http://localhost:5001';
        } else {
          // In production, use same origin
          apiBaseUrl = '';
        }
      }

      const configUrl = `${apiBaseUrl}/api/config`;
      console.log('[Config] Loading configuration from backend:', configUrl);

      const response = await fetch(configUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
      }

      const backendConfig = await response.json();
      console.log('[Config] Configuration loaded from backend');

      // Cache the config
      backendConfigCache = backendConfig;
      return backendConfig;
    } catch (error) {
      console.warn('[Config] Failed to load config from backend, using fallback values:', error?.message || error);
      // Return empty object - will use env var fallbacks
      return {};
    } finally {
      configLoadPromise = null;
    }
  })();

  return configLoadPromise;
}

// Initialize config load on module load (non-blocking)
if (typeof window !== 'undefined') {
  loadBackendConfig().catch(() => {
    // Silent fail - will use fallback values
  });
}

const config = {
  // Environment
  env: process.env.NODE_ENV || 'development',
  isProduction,
  
  // Backend config loader
  loadBackendConfig,
  
  // Get backend config (synchronous - returns cached or empty)
  getBackendConfig: () => backendConfigCache || {},
  
  // Rate limiting configuration
  rateLimiting: {
    // Base delay between requests in milliseconds
    delay: parseInt(process.env.REACT_APP_RATE_LIMIT_DELAY || '1000', 10),
    // Maximum concurrent requests
    maxConcurrent: parseInt(process.env.REACT_APP_MAX_CONCURRENT_REQUESTS || '2', 10),
    // Rate limit window in milliseconds (1 minute)
    window: parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW || '60000', 10),
    // Maximum retry attempts
    maxRetries: 3,
    // Initial backoff delay in milliseconds
    initialBackoff: 1000,
  },
  
  // Cache settings
  cache: {
    // Cache expiration time in milliseconds (24 hours)
    cacheExpiry: 24 * 60 * 60 * 1000,
    // Product cache settings
    productCacheKey: 'kiosk_products_cache',
    productCacheTimestampKey: 'kiosk_products_timestamp',
    // User cache settings
    userCacheKey: 'kiosk_users_cache',
    userCacheTimestampKey: 'kiosk_users_timestamp',
    // Order cache settings
    orderCacheKey: 'kiosk_order_cache',
    // Session settings
    sessionExpiry: 60 * 60 * 1000, // 1 hour
  },
  
  // Backend API Configuration
  get api() {
    const backend = this.getBackendConfig();
    return {
      // Base URL for all API requests (from backend or env fallback)
      baseUrl: backend.api?.baseUrl || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
      timeout: 15000, // 15 seconds
      
      // API endpoints (backend)
      endpoints: {
        products: '/api/products',
        users: '/api/users',
        orders: '/orders',
        paymentGateways: '/kiosk-payment-gateways',
        cacheStatus: '/cache-status',
        clearCache: '/clear-cache',
      },
      
      // Cache settings for frontend
      cache: {
        enabled: true,
        defaultTtl: 5 * 60 * 1000 // 5 minutes
      }
    };
  },
  
  // Stripe Configuration (only public key is needed in frontend)
  get stripe() {
    const backend = this.getBackendConfig();
    return {
      publicKey: backend.stripe?.publicKey || process.env.REACT_APP_STRIPE_PUBLIC_KEY || '',
      apiVersion: '2023-10-16',
      currency: 'USD',
      country: 'US',
      terminal: {
        locationId: backend.stripe?.terminal?.locationId || process.env.REACT_APP_STRIPE_TERMINAL_LOCATION_ID || '',
        connectionTimeout: 30000, // 30 seconds
        maxConnectionAttempts: 3,
      }
    };
  },
  
  
  // Feature flags
  get features() {
    const backend = this.getBackendConfig();
    return {
      enableStripeTerminal: backend.features?.enableStripeTerminal ?? (process.env.REACT_APP_ENABLE_STRIPE_TERMINAL === 'true')
      // Note: enableManualPayments and enableReceiptPrinting excluded per user request
    };
  },
  
  // Guest checkout configuration
  get guestCheckout() {
    const backend = this.getBackendConfig();
    return {
      donationAccountEmail: backend.guestCheckout?.donationAccountEmail || process.env.REACT_APP_GUEST_CHECKOUT_EMAIL || 'devotee@icctmemphis.org',
      defaultDonationAccountName: 'Guest Account',
      guestUserId: backend.guestCheckout?.guestUserId || parseInt(process.env.REACT_APP_GUEST_CHECKOUT_USER_ID || '1', 10)
    };
  },
  
  // UI/Payment configuration (fees, etc.)
  get ui() {
    const backend = this.getBackendConfig();
    const baseUi = {
      defaultPageSize: 25,
      showVariationsInList: true,
      currency: {
        symbol: '$',
        code: 'USD',
        precision: 2,
        thousand: ',',
        decimal: '.',
        format: '%s%v', // %s is the symbol, %v is the value
      },
      // Fee configuration from backend or env fallback
      feePercentage: backend.ui?.feePercentage || parseFloat(process.env.REACT_APP_CREDIT_CARD_FEE_PERCENTAGE || '2.5'),
      flatFee: backend.ui?.flatFee || parseFloat(process.env.REACT_APP_FLAT_FEE || '1.0'),
      feeName: backend.ui?.feeName || process.env.REACT_APP_CREDIT_CARD_FEE_NAME || 'Credit Card Processing Fee'
    };
    
    return baseUi;
  },
  
  // Kotilinga configuration
  get kotilinga() {
    const backend = this.getBackendConfig();
    const categorySlugs = (() => {
      if (Array.isArray(backend.kotilinga?.categorySlugs) && backend.kotilinga.categorySlugs.length) {
        return backend.kotilinga.categorySlugs.map((s) => String(s).trim().toLowerCase()).filter(Boolean);
      }
      const env = process.env.REACT_APP_KOTILINGA_CATEGORY_SLUGS;
      if (env) {
        return String(env).split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      }
      return ['kadambavanam'];
    })();
    return {
      categorySlugs,
      categorySlugsCsv: categorySlugs.join(','),
      productCategoryIds: backend.kotilinga?.productCategoryIds || [],
      productCategories: backend.kotilinga?.productCategories || (process.env.REACT_APP_KOTILINGA_PRODUCT_CATEGORIES ? 
        process.env.REACT_APP_KOTILINGA_PRODUCT_CATEGORIES.split(',').map(s => s.trim()).filter(Boolean) : []),
      products: backend.kotilinga?.products || (process.env.REACT_APP_KOTILINGA_PRODUCTS ? 
        process.env.REACT_APP_KOTILINGA_PRODUCTS.split(',').map(s => s.trim()).filter(Boolean) : [])
    };
  },
  
  // Payment defaults
  get payment() {
    const backend = this.getBackendConfig();
    return {
      defaultMethod: backend.payment?.defaultMethod || process.env.REACT_APP_WC_PAYMENT_METHOD || 'stripe_cc',
      defaultMethodTitle: backend.payment?.defaultMethodTitle || process.env.REACT_APP_WC_PAYMENT_METHOD_TITLE || 'Credit Card',
      defaultCurrency: backend.payment?.defaultCurrency || process.env.REACT_APP_WC_PAYMENT_CURRENCY || 'usd',
      defaultStatus: backend.payment?.defaultStatus || process.env.REACT_APP_WC_PAYMENT_STATUS || 'pending',
      defaultCustomerNote: backend.payment?.defaultCustomerNote || process.env.REACT_APP_WC_PAYMENT_CUSTOMER_NOTE || 'Kiosk order',
      setPaid: backend.payment?.setPaid ?? (process.env.REACT_APP_WC_PAYMENT_SET_PAID === 'true')
    };
  },
  
  // Quick add products
  get quickAdd() {
    const backend = this.getBackendConfig();
    return {
      products: backend.quickAdd?.products || (process.env.REACT_APP_QUICK_ADD_PRODUCTS ? 
        process.env.REACT_APP_QUICK_ADD_PRODUCTS.split(',').map(s => s.trim()).filter(Boolean) : [])
    };
  },
  
  // Kiosk ID
  get kioskId() {
    const backend = this.getBackendConfig();
    return backend.kiosk?.kioskId || process.env.REACT_APP_KIOSK_ID || 'default-kiosk';
  },
  
  // Logging and debugging
  debug: {
    logApiCalls: process.env.REACT_APP_LOG_API_CALLS === 'true',
    logStateChanges: process.env.REACT_APP_LOG_STATE_CHANGES === 'true',
    mockPayments: process.env.REACT_APP_MOCK_PAYMENTS === 'true',
  },
  
  // Validate required settings
  validate: function() {
    const required = [
      { key: 'api.baseUrl', name: 'REACT_APP_API_BASE_URL' },
      { key: 'stripe.publicKey', name: 'REACT_APP_STRIPE_PUBLIC_KEY' }
    ];
    
    const missing = [];
    required.forEach(({ key, name }) => {
      const value = key.split('.').reduce((o, k) => o && o[k], this);
      if (!value) {
        console.error(`Missing required setting: ${name}`);
        missing.push(name);
      }
    });
    
    return missing.length === 0;
  },
  
  // Initialize the configuration
  init: function() {
    // Validate required settings
    const isValid = this.validate();
    if (!isValid) {
      console.warn('Some required configuration is missing. The app may not work as expected.');
    }
    
    // In production, don't log sensitive information
    if (this.isProduction) {
      // Override console methods in production
      const originalConsole = { ...console };
      console.log = () => {};
      console.warn = () => {};
      console.info = () => {};
      console.error = originalConsole.error; // Keep error logging
    }
    
    return this;
  }
};

// Initialize and freeze the config object
const appConfig = Object.freeze(config.init());

export default appConfig;
